import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import {
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
  readableCloseHourToday,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { IOperatingHours } from 'generated/graphql-gateway';
import { useConfigValue } from 'hooks/configs/use-config-value';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { ServiceMode } from 'state/service-mode';

const getOperatingHours = (restaurant: IRestaurant) => (serviceMode: ServiceMode) => {
  // TODO: replace what's below with getStoreOperatingHoursForServiceMode from src/utils/restaurant/index.ts
  switch (serviceMode) {
    case ServiceMode.DRIVE_THRU:
      return restaurant.driveThruHours;
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return restaurant.mobileOrderDriveThruHours;
    case ServiceMode.CURBSIDE:
      return restaurant.curbsideHours;
    default:
      return restaurant.diningRoomHours;
  }
};

const isWithinOneHourOfClose = (
  hours: IOperatingHours,
  storeTimeZone: string | null | undefined
) => {
  return !isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
    hours,
    new Date(Date.now()),
    storeTimeZone || undefined,
    60
  );
};

interface IOpenClosedText {
  isOpen: boolean;
  isWithinOneHourOfClose: boolean;
  text: string;
}

export const useOpenClosedText = (store: IRestaurant): IOpenClosedText => {
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const { availablePickupServiceModes } = useServiceModeStatus(store);
  const { formatMessage } = useIntl();

  // Get the open hours for the first available pickup service mode.
  const [operatingHours] = availablePickupServiceModes.map(getOperatingHours(store));

  if (!operatingHours) {
    return { isOpen: false, isWithinOneHourOfClose: false, text: formatMessage({ id: 'closed' }) };
  }

  const closesWithinOneHour = isWithinOneHourOfClose(operatingHours, store.timezone);

  const text = closesWithinOneHour
    ? formatMessage(
        { id: 'closesAt' },
        { time: readableCloseHourToday(operatingHours, timeFormatConfig, store.timezone) }
      )
    : formatMessage({ id: 'open' });

  return {
    isOpen: true,
    isWithinOneHourOfClose: closesWithinOneHour,
    text,
  };
};
